import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { ButtonGroup } from '@monorepo/base/src/components/buttons/group/button-group';
import { GButton } from '@monorepo/base/src/components/buttons/group/button/g-button';
// import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { SelectionList } from '@monorepo/base/src/components/selection-list/selection-list';
import { Card } from '@monorepo/base/src/components/card/card';
import { DealTypes } from '../../../../enums/deal-types';
import { useList } from '../../../../hooks/tools/use-list';
import { DealModel } from '../../../../models/deal.model';
import { StrategyModel } from '../../../../models/strategy.model';
import { IAdminxStore } from '../../../../stores';
import { SelectionListOption } from '../../../selection-list-option/selection-list-option';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import {
	defaultBillingForPreferredDealType,
	defaultStrateryForPreferredDealType,
	defaultStrategyForPrivateAuctionType,
	defaultStrategyForGuaranteedDealType,
} from '../deal-type-strategies';

export {
	defaultStrateryForPreferredDealType,
	defaultStrategyForPrivateAuctionType,
	defaultStrategyForGuaranteedDealType,
} from '../deal-type-strategies';

interface DealOption {
	name: string;
	description: string;
}

export const Pretargeting: FC = observer(() => {
	const { campaignStore, dealStore } = useStores<IAdminxStore>();

	const campaignCrudStore = campaignStore.getCrud();
	const formStore = campaignCrudStore.getFormStore();
	const inputOptions = campaignStore.getInputOptions();
	const strategies = inputOptions.getStrategies();
	const pretargetings = inputOptions.getPretargetings();
	const campaign = campaignStore.getCrud().getData();
	const auctionType = campaign.getAuctionType();
	const strategy = campaign.getStrategy();
	const deals = dealStore.getListStore().getList();

	const { currentRouteWithoutSpecialChars } = useRoute();
	useList<DealModel>(dealStore.getListStore());

	const programmaticGuaranteedOptions = useMemo(
		() =>
			deals
				.filter(deal => deal.getDealType() === 'PROGRAMMATIC_GUARANTEED')
				.map(deal => ({
					name: `${deal.getDealId()}` || '',
					description: `${deal.getDealName()}` || '',
				})),
		[deals]
	);
	const preferredDealOptions = useMemo(
		() =>
			deals
				.filter(deal => deal.getDealType() === 'PREFERRED_DEAL')
				.map(deal => {
					return {
						name: `${deal.getDealId()}` || '',
						description: `${deal.getDealName()}` || '',
					};
				}),
		[deals]
	);

	const privateDealOptions = useMemo(
		() =>
			deals
				.filter(deal => deal.getDealType() === 'PRIVATE_AUCTION')
				.map(deal => {
					return {
						name: `${deal.getDealId()}` || '',
						description: `${deal.getDealName()}` || '',
					};
				}),
		[deals]
	);

	const onDealType = (setValues: Set<string>) => {
		const _pretargetingType = setValues.values().next().value as DealTypes;

		if (_pretargetingType === DealTypes.PreferredDeal) {
			const resetDropdowns = () => {
				const strategy = strategies.find(_strategy => _strategy.name === defaultStrateryForPreferredDealType);

				if (strategy) {
					campaign.getPhases().forEach(p => {
						p.setStrategy(new StrategyModel(strategy));
					});
				}
				formStore.clearError('billing_ids');
				campaign.setBillingIds([defaultBillingForPreferredDealType]);
				campaign.setDeals([]);
				campaign.setAuctionType(_pretargetingType);
			};
			resetDropdowns();
		}

		if (_pretargetingType === DealTypes.PrivateAuction) {
			const resetDropdowns = () => {
				const strategy = strategies.find(_strategy => _strategy.name === defaultStrategyForPrivateAuctionType);
				if (strategy) {
					campaign.getPhases().forEach(p => {
						p.setStrategy(new StrategyModel(strategy));
					});
				}

				formStore.clearError('billing_ids');
				campaign.setBillingIds([defaultBillingForPreferredDealType]);
				campaign.setDeals([]);
				campaign.setAuctionType(_pretargetingType);
			};

			resetDropdowns();
		}

		if (_pretargetingType === DealTypes.GuaranteedDeal) {
			const resetDropdowns = () => {
				const strategy = strategies.find(s => s.name === defaultStrategyForGuaranteedDealType);
				if (strategy) {
					campaign.getPhases().forEach(p => p.setStrategy(new StrategyModel(strategy)));
				}
				formStore.clearError('billing_ids');
				campaign.setBillingIds([defaultBillingForPreferredDealType]);
				campaign.setDeals([]);
				campaign.setAuctionType(_pretargetingType);
			};
			resetDropdowns();
		}

		if (_pretargetingType === DealTypes.OpenAuction) {
			campaign.setBillingIds(undefined);
			campaign.getPhases().forEach(p => p.setStrategy(undefined));
			campaign.setDeals([]);
		}

		campaign.setAuctionType(_pretargetingType);
	};

	const onDealSelect = (options: DealOption[]) => {
		const _selectedDeals = options.map(option =>
			deals.find(deal => {
				return option.name === `${deal.getDealId()}`;
			})
		);
		const dealsArr: DealModel[] = [];
		_selectedDeals.forEach(_deal => {
			if (_deal) {
				dealsArr.push(new DealModel(_deal));
			}
		});
		campaign.setDeals(dealsArr);
	};

	const selectedDeals = campaign.getDeals()?.map(deal => {
		return {
			name: `${deal.getDealId()}` || '',
			description: `${deal.getDealName()}` || '',
		};
	});

	const [strategyName, setStartegyName] = useState<string>('');
	useEffect(() => {
		if (strategy?.name && strategy?.getId() && strategyName !== `${strategy?.name} (${strategy?.getId()})`) {
			setStartegyName(`${strategy?.name} (${strategy?.getId()})`);
		}
	});

	return (
		<Card
			id={'pretargeting'}
			title={'Pretargeting'}
			icon={'package-search'}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'pretargeting_section')] }}>
			<Spacer y={2} />
			<ButtonGroup title={'Type'} defaultValue={auctionType} onChange={onDealType} required={true}>
				<GButton
					icon={'gavel'}
					text={DealTypes.OpenAuction}
					value={DealTypes.OpenAuction}
					disabled={currentRouteWithoutSpecialChars === 'campaignsedit'}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'pretargeting_type_open_auction')] }}
				/>
				<GButton
					icon={'handshake'}
					text={DealTypes.PreferredDeal}
					value={DealTypes.PreferredDeal}
					disabled={currentRouteWithoutSpecialChars === 'campaignsedit'}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'pretargeting_type_preferred_deal')] }}
				/>
				<GButton
					icon={'lock-01'}
					text={DealTypes.PrivateAuction}
					value={DealTypes.PrivateAuction}
					disabled={currentRouteWithoutSpecialChars === 'campaignsedit'}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'pretargeting_type_private_auction')] }}
				/>
				<GButton
					icon={'check-verified-01'}
					text={DealTypes.GuaranteedDeal}
					value={DealTypes.GuaranteedDeal}
					disabled={currentRouteWithoutSpecialChars === 'campaignsedit'}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'pretargeting_type_programmatic_guaranteed')] }}
				/>
			</ButtonGroup>
			{auctionType === DealTypes.OpenAuction && (
				<Dropdown
					required
					defaultOptions={
						new Set(campaign.getBillingIds()?.map(id => pretargetings.find(p => p.billing_id === id)?.name || '') || [])
					}
					label={'Select Billing IDs'}
					multi={true}
					autocomplete
					options={pretargetings.map(_pretargeting => _pretargeting.name)}
					onSelect={(val: Set<option> | undefined) => {
						campaign.setBillingIds(val ? pretargetings.filter(p => val.has(p.name)).map(p => p.billing_id) : []);
						formStore.clearError('billing_ids');
					}}
					error={formStore.getErrors().get('billing_ids')?.getMsg()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'billing_ids')] }}
				/>
			)}
			{auctionType === DealTypes.PreferredDeal && (
				<Fragment>
					<Spacer y={2} />

					<SelectionList
						label={'Deals'}
						options={preferredDealOptions}
						defaultOptions={selectedDeals}
						onSelect={(deal: DealOption[]) => onDealSelect(deal)}
						isLoadingOptions={campaignStore.getListStore().getIsLoading()}
						isSelectionList={true}
						renderOption={option => (
							<SelectionListOption option={option} debugProps={{ dataAttrs: [new DataAttribute('id', 'deals')] }} />
						)}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'deals_dropdown')] }}
					/>
				</Fragment>
			)}
			{auctionType === DealTypes.PrivateAuction && (
				<Fragment>
					<Spacer y={2} />

					<SelectionList
						label={'Deals'}
						options={privateDealOptions}
						defaultOptions={selectedDeals}
						onSelect={(deal: DealOption[]) => onDealSelect(deal)}
						isLoadingOptions={campaignStore.getListStore().getIsLoading()}
						isSelectionList={true}
						renderOption={option => (
							<SelectionListOption option={option} debugProps={{ dataAttrs: [new DataAttribute('id', 'private_deals')] }} />
						)}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'private_deals_dropdown')] }}
					/>
				</Fragment>
			)}
			{auctionType === DealTypes.GuaranteedDeal && (
				<Fragment>
					<Spacer y={2} />

					<SelectionList
						label={'Deals'}
						options={programmaticGuaranteedOptions}
						defaultOptions={selectedDeals}
						onSelect={(deal: DealOption[]) => onDealSelect(deal)}
						isLoadingOptions={campaignStore.getListStore().getIsLoading()}
						isSelectionList={true}
						renderOption={option => (
							<SelectionListOption option={option} debugProps={{ dataAttrs: [new DataAttribute('id', 'deals')] }} />
						)}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'deals_dropdown')] }}
					/>
				</Fragment>
			)}
		</Card>
	);
});
